import {ObjectRepository} from "../api/repositories/ObjectRepository";
import {UserProfile} from "../api/models/UserProfile";
import {RepositoryFabric} from "../api/repositories/RepositoryFabric";
import {QueryParametersInterface} from "../api/repositories/interfaces/QueryParametersInterface";
import {UserRepository} from "../api/repositories/UserRepository";
import Axios from "axios";
import {
    prepareProfileFilter,
    prepareRecommendedCondition,
    getOrderSettings,
    prepareUserIdsFilter
} from '../api/helpers/profileSearch';
import {groupFilter} from "../api/helpers/groupFilter";
import {Store} from "vuex";
import { EventMembershipTypes } from '../api/enums/EventMembershipTypes';
import { SelectableTag } from "../api/models/SelectableTag";
import {CustomParticipantsData} from "../share/participants-table/types/CustomParticipantsData";
import { getIgnoringQueryObject } from "../service/helpers/exclusionGroupHelper";
import Vue from "vue";

/**
 * Helper contains data fetching logic for Participants component
 */
export class ParticipantsDataHelper {
    private readonly objectRepository: ObjectRepository;
    private readonly userRepository: UserRepository;
    list: UserProfile[] = [];
    page =  1;
    take = 20;
    count = 0;
    private cancelToken: any = null;
    searchStr = '';
    isLoading = false;
    include: string[] = ['lastName', 'firstName', 'company', 'position', 'userId', 'id', 'photoFileId', 'biography', 'updatedAt', 'interestingTagIds'];
    additionalFields: string[] = [];
    groupFilter: any;
    onlineOnly = false;
    recommendedOnly = false;
    filterOrder = "company";
    filterDirection: "asc" | "desc" = "asc";
    participantTypes: EventMembershipTypes[] = [];
    tags: SelectableTag[] = [];
    tagsText: Record<number, string> = {};

    constructor(
        repositories: RepositoryFabric,
        private readonly eventId: string,
        private readonly userId: number = 0,
        groupIds: string[],
        private readonly store: Store<any>,
        private readonly filterUserIds: number[] = [],
        private readonly customData: CustomParticipantsData = {},
        public readonly sortOnClient = false
    ) {
        this.objectRepository= repositories.get(RepositoryFabric.OBJECT);
        this.userRepository = repositories.get(RepositoryFabric.USER);
        this.groupFilter = groupFilter(groupIds || []);
    }

    /**
     * Prepares condition for user fetching
     */
    async prepareCondition(): Promise<any> {
        let result: any = {};
        const selectedTagIds = this.tags.filter((item) => item.isSelected).map((item) => item.tag.id)

        result = prepareProfileFilter({
            searchStr: this.searchStr,
            additionalFields: this.additionalFields,
            excludingUsers: [],
            groupFilter: this.groupFilter,
            eventId: this.eventId,
            participantTypes: this.participantTypes,
            tagsIds: selectedTagIds
        }) || {};

        if (this.filterUserIds && this.filterUserIds.length) {
            result.$and?.push(prepareUserIdsFilter(this.filterUserIds));
        }

        if (this.recommendedOnly) {

            const profile = this.store.getters.profile as UserProfile | undefined | null;

            if (profile?.tagsIds?.length && profile?.interestingTagIds?.length) {
                result.$and?.push(prepareRecommendedCondition(profile?.tagsIds, profile?.interestingTagIds));
                result.$and?.push({ userId: { $nin: profile?.userId } });
            }
        }

        if(this.onlineOnly) {
            result.$and.push({ userId: { $inQuery: {'$call GetOnlineUserIds': ["general"]}}});
        }

        const ignoredUserGroupIds = this.store.getters['ignoredUserGroupIds'];

        if (ignoredUserGroupIds) {
            result.$and.push(getIgnoringQueryObject(ignoredUserGroupIds))
        }

        return result;
    }

    /**
     * Fetching user profiles, if page == 1, then also fetch count of students to be able to do paging
     */
    async fetchUsers() {
        this.isLoading = true;
        if (this.cancelToken) {
            this.cancelToken.cancel();
        }

        const order: any = getOrderSettings(this.searchStr, this.filterDirection, this.sortOnClient ? undefined : this.filterOrder);

        const where = await this.prepareCondition(),
            query: QueryParametersInterface = {
                take: this.take,
                skip: this.list.length,
                order,
                include: this.include.concat(this.additionalFields),
                where
            };
        this.cancelToken = Axios.CancelToken.source();
        try {
            const isFirstPage = this.page == 1;

            if (isFirstPage) {
                query.skip = 0;
                this.list = [];
                this.tagsText = {};
            }

            let { profiles, count } = await this.userRepository.profilesWithCount(query, {
                cancelToken: this.cancelToken.token
            });

            this.list = isFirstPage
                ? profiles
                : this.list.concat(this.filterDuplicates(profiles));
            this.count = count;

            this.propagateCustomData();
            this.prepareTagsText(profiles);

            if (this.sortOnClient) {
                await this.sortList();
            }
        }
        catch (e) {
            this.store.dispatch('reportException', e, { root: true });
        }
        this.isLoading = false;
    }

    /**
     * Assign custom fields to user instances
     * @private
     */
    private propagateCustomData() {
        if (this.customData && Object.keys(this.customData).length) {
            this.list.map(userProfile => {
                if (this.customData[userProfile.userId as number]) {
                    const userCustomData = this.customData[userProfile.userId as number];
                    Object.keys(userCustomData).forEach(customDataKey => {
                       userProfile[customDataKey] = userCustomData[customDataKey];
                    });
                }
            })
        }
    }

    /**
     * Sorts list
     */
    async sortList() {
        this.list = this.list.sort((a, b) => {
            if (a[this.filterOrder] > b[this.filterOrder]) {
                return this.filterDirection == "asc" ? 1 : -1;
            }

            if (a[this.filterOrder] < b[this.filterOrder]) {
                return this.filterDirection == "asc" ? -1 : 1;
            }

            return 0;
        });
    }

    /**
     * Fetch update info for user profiles - [{ id, updatedAt }, ...]
     * @param profiles
     */
    async fetchUserUpdateInfo(profiles: UserProfile[]) {
        return this.userRepository.profileUpdateInfo(profiles.map(profile => profile.id));
    }

    async fetchUsersByIds(ids: string[]): Promise<UserProfile[]> {
        const query = {
            where: {
                id: {
                    $in: ids
                }
            },
            include: this.include,
            skip: 0,
            take: -1
        }
        return this.userRepository.profiles(query)
    }

    private filterDuplicates(data: UserProfile[]) {
        const clonedData = [...data];
        const currentProfileIds = this.list.map(item => item.id);
        return clonedData.filter(item => currentProfileIds.indexOf(item.id) < 0);
    }

    private prepareTagsText(profiles: UserProfile[]) {
        profiles.forEach(item => {
            const userLookingForIds = item.getField("interestingTagIds");
            let tagsText =  '';
            if(userLookingForIds && userLookingForIds.length > 0) {
                tagsText = this.tags.filter((item) => userLookingForIds.includes(item.tag.id)).map((tag) => tag.tag.title).join(", ");
            }
            Vue.set(this.tagsText, item.userId as number, tagsText);
        })
    }

    clean() {
        this.tagsText = {};
        this.list = [];
        this.tags = [];
    }
}